import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useIsomorphicLayoutEffect, useResizeObserver } from '@direct-frontend/stdlib';
import { useControlledState } from '@react-stately/utils';

import { cn } from '../../../lib/css/className/className';
import { VStack } from '../../Stack';
import { useDrag } from '../helpers/useDrag';
import { useMoveBackward } from '../helpers/useMoveBackward';
import { useMoveForward } from '../helpers/useMoveForward';
import { useMoveToCurrentSlide } from '../helpers/useMoveToCurrentSlide';
import { useCursor } from '../helpers/useCursor';
import './GalleryWithPaginator.css';
import { OverlayButtons } from '../OverlayButtons/OverlayButtons';
import { GalleryWithPaginatorProps, ItemProps } from '../types';
import { Dots } from '../../Dots/Dots';

const cls = cn('GalleryWithPaginator');

/**
 * Компонент для переключения слайдов с помощью кнопок или метода dragAndDrop
 * Слайды растягиваются на всю ширину
 * Компонент имеет кнопки по бокам слайдера и пагинацию
 */
export const GalleryWithPaginator = function GalleryWithPaginator<ItemPropsT extends ItemProps>({
    'data-testid': dataTestId = 'GalleryWithPaginator',
    className,
    arrowButtons,
    items,
    itemGapSize = '8',
    renderItemContent,
    hidePagination = false,
    hidePaginationForSingleItem = false,
    animationDuration = 300,
    currentSlide,
    onSwipe,
    getSwipeCount,
}: GalleryWithPaginatorProps<ItemPropsT>) {
    const listContainerRef = useRef<HTMLDivElement>(null);
    const slides = useRef<Map<number, HTMLDivElement>>(new Map());

    const [translateX, setTranslateX] = useState(0);
    const [currentSlideIndex, setCurrentSlideIndex] = useControlledState(currentSlide, 0, onSwipe);
    const [isDragging, setIsDragging] = useState(false);
    const [lastSwipeSlideIndex, setLastSwipeSlideIndex] = useState(0);
    const [listWidth, setListWidth] = useState(0);
    const moveToCurrentSlideHandler = useMoveToCurrentSlide();
    const onResize = useCallback(() => {
        if (listContainerRef.current) {
            setListWidth(listContainerRef.current?.offsetWidth);
        }

        setLastSwipeSlideIndex(Math.max(items.length - 1, 0));
    }, [items]);
    const resizeObserver = useResizeObserver(onResize);
    const shouldHidePagination = hidePagination || (items.length === 1 && hidePaginationForSingleItem);

    useEffect(() => {
        // Следит за изменением ширины контейнера
        if (resizeObserver && listContainerRef.current) {
            resizeObserver.observe(listContainerRef.current);
        }
    }, [resizeObserver]);

    useIsomorphicLayoutEffect(() => {
        if (listContainerRef.current) {
            setListWidth(listContainerRef.current?.offsetWidth);
        }
    }, [listContainerRef]);

    useEffect(() => {
        moveToCurrentSlideHandler({
            slides,
            currentSlideIndex,
            isDragging,
            listContainerRef,
            setTranslateX,
        });
    }, [currentSlideIndex, isDragging, listWidth, moveToCurrentSlideHandler]);

    const onDotClick = useCallback(
        (_item: ItemProps, index: number) => {
            setCurrentSlideIndex(index);
        },
        [setCurrentSlideIndex],
    );

    const moveForward = useMoveForward({
        currentSlideIndex,
        lastSwipeSlideIndex,
        setCurrentSlideIndex,
    });

    const moveBackward = useMoveBackward({
        currentSlideIndex,
        setCurrentSlideIndex,
    });

    const forwardButtonProps = useMemo(() => {
        const isLastSlide = currentSlideIndex === items.length - 1;

        return {
            canMoveForward: !isLastSlide,
            onClick: moveForward,
        };
    }, [currentSlideIndex, items, moveForward]);

    const backwardButtonProps = useMemo(
        () => ({
            canMoveBackward: currentSlideIndex !== 0,
            onClick: moveBackward,
        }),
        [currentSlideIndex, moveBackward],
    );

    const bindDrag = useDrag({
        setIsDragging,
        canMoveBackward: backwardButtonProps.canMoveBackward,
        canMoveForward: forwardButtonProps.canMoveForward,
        moveBackward,
        moveForward,
        getSwipeCount,
        gestureProps: {
            setTranslateX,
            translateX,
            setCurrentSlideIndex,
            slides,
            lastSwipeSlideIndex,
        },
    });

    const cursorStyle = useCursor({
        isDragging,
        canMoveBackward: backwardButtonProps.canMoveBackward,
        canMoveForward: forwardButtonProps.canMoveForward,
    });

    const listStyle = useMemo(
        () => ({
            gap: `${itemGapSize}px`,
            transform: `translate3d(${translateX}px, 0, 0)`,
            transition: `${isDragging ? 0 : animationDuration}ms`,
            cursor: cursorStyle,
        }),
        [itemGapSize, translateX, isDragging, animationDuration, cursorStyle],
    );

    useEffect(() => {
        if (slides.current.size > items.length) {
            slides.current.clear();
        }
    }, [items.length]);

    return (
        <VStack data-testid={dataTestId} className={cls('', [className])} gap="12">
            <VStack>
                <div ref={listContainerRef} className={cls('listContainer')}>
                    {listWidth && (
                        <div {...bindDrag()} className={cls('list')} style={listStyle}>
                            {/* Слайды */}
                            {items.map((item, index) => (
                                <div
                                    style={{
                                        width: `${listWidth}px`,
                                    }}
                                    key={item.id}
                                    data-item-id={item.id}
                                    ref={el => {
                                        if (el) {
                                            slides.current.set(index, el);
                                        }
                                    }}
                                >
                                    {renderItemContent(item, currentSlideIndex)}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <OverlayButtons
                    className={arrowButtons?.className}
                    color={arrowButtons?.color}
                    size={arrowButtons?.size}
                    onClickBackward={backwardButtonProps.onClick}
                    onClickForward={forwardButtonProps.onClick}
                    isHiddenBackward={arrowButtons?.isHidden || !backwardButtonProps.canMoveBackward}
                    isHiddenForward={arrowButtons?.isHidden || !forwardButtonProps.canMoveForward}
                />
            </VStack>
            {/* Пагинация - точки */}
            {!shouldHidePagination && <Dots items={items} selectedIndex={currentSlideIndex} onClick={onDotClick} />}
        </VStack>
    );
};
